import {
  createContext,
  useContext,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
  type RefObject,
} from 'react';

import { Tooltip, TooltipProvider } from '@/components/ui/Tooltip';

const TranscriptTooltipSession = createContext<RefObject<boolean> | null>(null);

export function TranscriptTooltipSessionProvider({
  children,
}: {
  children: ReactNode;
}) {
  const parentSession = useContext(TranscriptTooltipSession);
  // ponytail: page-local memory; no persisted dismissal across visits
  const hasShown = useRef(false);

  return (
    <TranscriptTooltipSession.Provider value={parentSession ?? hasShown}>
      {children}
    </TranscriptTooltipSession.Provider>
  );
}

export function TranscriptTooltip({
  children,
  disabled,
}: {
  children: ReactElement;
  disabled: boolean;
}) {
  const hasShown = useContext(TranscriptTooltipSession);
  const [open, setOpen] = useState(false);
  if (!hasShown) throw new Error('Transcript tooltip requires a page session');

  return (
    <TooltipProvider skipDelayDuration={0}>
      <Tooltip
        label="Click to select, right-click or touch and hold a word to correct it."
        delayDuration={1500}
        disabled={disabled}
        open={open}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            setOpen(false);
          } else if (!hasShown.current) {
            hasShown.current = true;
            setOpen(true);
          }
        }}
      >
        {children}
      </Tooltip>
    </TooltipProvider>
  );
}
